import * as SheetPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { X, ArrowLeft } from '@phosphor-icons/react'
import React from 'react'

import { cn } from '@/lib/utils'

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = ({ className, ...props }: React.ComponentPropsWithRef<typeof SheetPrimitive.Overlay>) => (
  <SheetPrimitive.Overlay
    className={cn(
      `sheet-overlay-blocker fixed inset-0 z-40 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`,
      className
    )}
    {...props}
  />
)
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  `fixed z-40 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 flex flex-col`,
  {
    variants: {
      side: {
        top: `inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top`,
        bottom: `inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom`,
        left: `inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left`,
        right: `inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right`
      }
    },
    defaultVariants: {
      side: `right`
    }
  }
)

interface SheetContentProps extends React.ComponentPropsWithRef<typeof SheetPrimitive.Content>, VariantProps<typeof sheetVariants> {
  multiple?: boolean
}

type SheetPointerDownOutsideEvent = Parameters<NonNullable<React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>['onPointerDownOutside']>>[0]

const SheetContent = ({ side = `right`, className, children, multiple, onPointerDownOutside, ...props }: SheetContentProps) => {
  const handlePointerDownOutside = (event: SheetPointerDownOutsideEvent) => {
    const body = typeof document === `undefined` ? null : document.body

    if (body?.dataset?.sidePanelOpen === `true`) {
      const panelRoot = document.querySelector<HTMLElement>(`[data-sidepanel-root]`)
      const { originalEvent } = event.detail
      const path = (originalEvent.composedPath?.() ?? []) as Node[]

      if (panelRoot && path.some(node => node && panelRoot.contains(node))) {
        event.preventDefault()
        event.stopPropagation()
        originalEvent.stopPropagation()
        originalEvent.stopImmediatePropagation()
        return
      }
    }

    onPointerDownOutside?.(event)
  }

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content {...props} onPointerDownOutside={handlePointerDownOutside} className={cn(sheetVariants({ side }), className)}>
        <div className='h-10' />
        <div className='flex flex-1 flex-col'>{children}</div>
        <SheetPrimitive.Close className='absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full opacity-70 ring-offset-background transition-opacity hover:bg-gray-100 hover:opacity-100 hover:ring-0 focus:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary'>
          <X size={20} className='text-secondary-foreground' />
          <span className='sr-only'>Close</span>
        </SheetPrimitive.Close>
        {multiple && (
          <SheetPrimitive.Close className='text-secondary-foreground absolute left-4 top-4 inline-flex h-8 items-center gap-2 rounded-sm px-2 py-1 opacity-70 ring-offset-background transition-opacity hover:bg-gray-100 hover:opacity-100 hover:ring-0 focus:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary'>
            <ArrowLeft size={20} />
            {` `}
            <span>Back</span>
            <span className='sr-only'>Back</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(`flex flex-col space-y-2 text-center sm:text-left`, className)} {...props} />
)
SheetHeader.displayName = `SheetHeader`

const SheetFooter = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(`mt-auto flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2`, className)} {...props}>
    <div className='mt-4 flex flex-1'>{children}</div>
  </div>
)
SheetFooter.displayName = `SheetFooter`

const SheetTitle = ({ className, ...props }: React.ComponentPropsWithRef<typeof SheetPrimitive.Title>) => (
  <SheetPrimitive.Title className={cn(`text-lg font-semibold text-foreground`, className)} {...props} />
)
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = ({ className, ...props }: React.ComponentPropsWithRef<typeof SheetPrimitive.Description>) => (
  <SheetPrimitive.Description className={cn(`text-sm text-muted-foreground`, className)} {...props} />
)
SheetDescription.displayName = SheetPrimitive.Description.displayName

export { Sheet, SheetPortal, SheetOverlay, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription }
